import format from 'date-fns-tz/format';
import utcToZonedTime from 'date-fns-tz/utcToZonedTime';
import formatISO from 'date-fns/formatISO';
import parseISO from 'date-fns/parseISO';
import { z } from 'zod';
import { AloftDate } from './AloftDate.js';
import { AloftObject } from './AloftObject.js';
export const schAloftTimeJSON = z.object({ $type: z.literal('AloftTime'), isoString: z.string() });
export class AloftTime extends AloftObject {
    value;
    constructor(value) {
        if (!(value instanceof Date) || Number.isNaN(value.getTime())) {
            throw new Error('Invalid date');
        }
        super();
        this.value = value;
    }
    static isAloftTime(x) {
        return x instanceof AloftTime;
    }
    compare(that) {
        return this.value.valueOf() - that.value.valueOf();
    }
    // NOTE: toJSON() and schema.parse() should be inverses of each other!
    toJSON() {
        return { $type: 'AloftTime', isoString: this.toIsoString() };
    }
    // just to be very explicit about having a function that our action
    // middleware knows to call if it needs to serialize this object for redux.
    toRedux() {
        return this.toJSON();
    }
    static schema = z
        .union([schAloftTimeJSON, z.custom((x) => x instanceof AloftTime)])
        .transform((x) => x instanceof AloftTime ? new AloftTime(x.value) : AloftTime.fromIsoString(x.isoString));
    static now() {
        return new AloftTime(new Date());
    }
    // Returns an IANA timezone string, not necessarily one listed in TimeZone
    static getCurrentTimeZone() {
        return Intl.DateTimeFormat().resolvedOptions().timeZone;
    }
    static fromIsoString(isoString) {
        if (isoString === null || isoString === undefined)
            return isoString;
        const x = parseISO(isoString);
        if (Number.isNaN(x.getTime())) {
            throw new Error(`Invalid ISO timestamp: ${x.toISOString()}`);
        }
        return new AloftTime(x);
    }
    static fromNativeDate(date) {
        if (date === null || date === undefined)
            return date;
        return new AloftTime(date);
    }
    static fromUnix(ts) {
        if (ts === null || ts === undefined)
            return ts;
        return AloftTime.fromNativeDate(new Date(ts));
    }
    toIsoString() {
        return formatISO(this.value);
    }
    toNativeDate() {
        return this.value;
    }
    toUnix() {
        return this.value.getTime();
    }
    toAloftDate(tz = AloftTime.getCurrentTimeZone()) {
        return AloftDate.fromIsoString(format(utcToZonedTime(this.value, tz), 'yyyy-MM-dd'));
    }
    formatTime(tz = AloftTime.getCurrentTimeZone(), formatTemplate = 'yyyy-MM-dd hh:mm aa') {
        return format(utcToZonedTime(this.value, tz), formatTemplate, { timeZone: tz });
    }
    addMinutes(minutes) {
        return AloftTime.fromNativeDate(new Date(this.value.getTime() + minutes * 60 * 1000));
    }
    removeMinutes(minutes) {
        return AloftTime.fromNativeDate(new Date(this.value.getTime() - minutes * 60 * 1000));
    }
}
//# sourceMappingURL=AloftTime.js.map